import { useMemo } from 'react'

import {
  CreateCategoryPolicyRuleDocument,
  DeleteCategoryPolicyRuleDocument,
  GetCategoryPolicyRulesDocument,
  UpdateCategoryPolicyRuleDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { AffecteeEntry } from 'modules/expenses/pages/settings/ExpensePolicy/CategorySettings/AffecteesSelector'

export interface CategoryPolicyRule {
  id: number
  companyId: number
  financeCustomCategoryId: number
  frequency: string
  limitAmountCents?: number | null
  peopleGroupAffectees: string | null
}

const serializeAffectees = (affectees: AffecteeEntry[]): string => {
  return JSON.stringify(affectees)
}

export const parseAffectees = (raw: string | null | undefined): AffecteeEntry[] | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

export const useReadCategoryPolicyRules = (categoryId: number) => {
  const { data, loading } = useQuery(GetCategoryPolicyRulesDocument, {
    variables: { financeCustomCategoryIds: [categoryId] },
  })

  const rules = useMemo<CategoryPolicyRule[]>(() => {
    const nodes = data?.expenses?.categoryPolicyRulesConnection?.nodes ?? []
    return nodes.map((node) => ({
      ...node,
      peopleGroupAffectees: node.peopleGroupAffectees ?? null,
    }))
  }, [data])

  return { rules, loading }
}

interface CreateRuleParams {
  companyId: number
  financeCustomCategoryId: number
  frequency: string
  limitAmountCents?: number | null
  peopleGroupAffectees?: AffecteeEntry[] | null
}

export const useCreateCategoryPolicyRule = () => {
  const [createRule, { loading }] = useMutation(CreateCategoryPolicyRuleDocument, {
    extractorFn: (data) => data.expensesMutations.createExpensesCategoryPolicyRule,
    refetchQueries: [GetCategoryPolicyRulesDocument],
    awaitRefetchQueries: true,
  })

  return {
    loading,
    create: (params: CreateRuleParams) =>
      createRule({
        variables: {
          ...params,
          peopleGroupAffectees:
            params.peopleGroupAffectees && params.peopleGroupAffectees.length > 0
              ? serializeAffectees(params.peopleGroupAffectees)
              : null,
        },
      }),
  }
}

interface UpdateRuleParams {
  id: number
  frequency: string
  limitAmountCents?: number | null
  peopleGroupAffectees?: AffecteeEntry[] | null
}

export const useUpdateCategoryPolicyRule = () => {
  const [updateRule, { loading }] = useMutation(UpdateCategoryPolicyRuleDocument, {
    extractorFn: (data) => data.expensesMutations.updateExpensesCategoryPolicyRule,
    refetchQueries: [GetCategoryPolicyRulesDocument],
    awaitRefetchQueries: true,
  })

  return {
    loading,
    update: (params: UpdateRuleParams) =>
      updateRule({
        variables: {
          ...params,
          peopleGroupAffectees:
            params.peopleGroupAffectees && params.peopleGroupAffectees.length > 0
              ? serializeAffectees(params.peopleGroupAffectees)
              : null,
        },
      }),
  }
}

export const useDeleteCategoryPolicyRule = () => {
  const [deleteRule, { loading }] = useMutation(DeleteCategoryPolicyRuleDocument, {
    extractorFn: (data) => data.expensesMutations.deleteExpensesCategoryPolicyRule,
    refetchQueries: [GetCategoryPolicyRulesDocument],
    awaitRefetchQueries: true,
  })

  return {
    loading,
    delete: (id: number) => deleteRule({ variables: { id } }),
  }
}
